import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Tag, Percent, Coins, Power } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APPLIES_LABEL = { meal_plan: 'Plan alimentaire', pro: 'Analyse Pro', subscription: 'Abonnements' };

export const AdminPromoCodesPanel = ({ csrfToken }) => {
  const [codes, setCodes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ code: '', discount_type: 'percent', discount_value: 100, applies_to: ['meal_plan'], max_uses: '', note: '' });

  const fetchWithCsrf = (url, opts = {}) => fetch(url, {
    ...opts,
    credentials: 'include',
    headers: { ...opts.headers, 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
  });

  const load = () => {
    fetch(`${BACKEND_URL}/api/admin/promo-codes`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setCodes(d.promo_codes || []));
  };
  useEffect(() => { load(); }, []);

  const toggleApplies = (key) => {
    setDraft((d) => ({ ...d, applies_to: d.applies_to.includes(key) ? d.applies_to.filter((a) => a !== key) : [...d.applies_to, key] }));
  };

  const createCode = () => {
    if (!draft.code.trim()) { toast.error('Nom du code requis.'); return; }
    if (draft.applies_to.length === 0) { toast.error('Sélectionnez au moins une catégorie.'); return; }
    setCreating(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/promo-codes`, {
      method: 'POST',
      body: JSON.stringify({
        code: draft.code.trim(), discount_type: draft.discount_type, discount_value: parseFloat(draft.discount_value) || 0,
        applies_to: draft.applies_to, max_uses: draft.max_uses ? parseInt(draft.max_uses, 10) : null, note: draft.note,
      }),
    }).then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then(() => { toast.success('Code promo créé !'); setDraft({ code: '', discount_type: 'percent', discount_value: 100, applies_to: ['meal_plan'], max_uses: '', note: '' }); load(); })
      .catch((e) => toast.error(e.detail || 'Erreur lors de la création.'))
      .finally(() => setCreating(false));
  };

  const toggleActive = (code, active) => {
    fetchWithCsrf(`${BACKEND_URL}/api/admin/promo-codes/${code}`, { method: 'PUT', body: JSON.stringify({ active: !active }) })
      .then(() => load());
  };

  const deleteCode = (code) => {
    if (!window.confirm(`Supprimer définitivement le code "${code}" ?`)) return;
    fetchWithCsrf(`${BACKEND_URL}/api/admin/promo-codes/${code}`, { method: 'DELETE' }).then(() => load());
  };

  if (!codes) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-panel">
      {/* Formulaire de création */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-white font-bold inline-flex items-center gap-2"><Plus className="w-4 h-4 text-primary" /> Nouveau code promo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            placeholder="BODYUP2026" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm uppercase focus:border-primary focus:outline-none"
            data-testid="promo-create-code-input" />
          <select value={draft.discount_type} onChange={(e) => setDraft({ ...draft, discount_type: e.target.value })}
            className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
            data-testid="promo-create-type-select">
            <option value="percent">Rabais en %</option>
            <option value="fixed">Montant fixe CHF</option>
          </select>
          <input type="number" min="0" step="0.5" value={draft.discount_value} onChange={(e) => setDraft({ ...draft, discount_value: e.target.value })}
            placeholder={draft.discount_type === 'percent' ? '100 = gratuit' : '10'} className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
            data-testid="promo-create-value-input" />
          <input type="number" min="0" value={draft.max_uses} onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })}
            placeholder="Utilisations max (vide = illimité)" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
            data-testid="promo-create-maxuses-input" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-zinc-500 text-xs">Applicable à :</span>
          {Object.entries(APPLIES_LABEL).map(([key, label]) => (
            <button key={key} type="button" onClick={() => toggleApplies(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${draft.applies_to.includes(key) ? 'bg-primary text-white border-transparent' : 'bg-zinc-800 text-zinc-400 border-white/10'}`}
              data-testid={`promo-create-applies-${key}`}>
              {label}
            </button>
          ))}
        </div>
        <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder="Note interne (facultatif)" className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
          data-testid="promo-create-note-input" />
        <button onClick={createCode} disabled={creating}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50"
          data-testid="promo-create-submit-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer le code
        </button>
      </div>

      {/* Liste des codes existants */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-promo-codes-table">
          <thead className="bg-zinc-800/70 text-zinc-400">
            <tr>
              {['Code', 'Rabais', 'Applicable à', 'Utilisations', 'Statut', 'Note', ''].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Aucun code promo créé</td></tr>}
            {codes.map((c) => (
              <tr key={c.code} className="border-t border-white/5 text-zinc-300" data-testid={`promo-row-${c.code}`}>
                <td className="px-4 py-3 text-primary font-bold inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {c.code}</td>
                <td className="px-4 py-3 text-white font-semibold inline-flex items-center gap-1">
                  {c.discount_type === 'percent' ? <Percent className="w-3.5 h-3.5 text-emerald-400" /> : <Coins className="w-3.5 h-3.5 text-yellow-400" />}
                  {c.discount_type === 'percent' ? `${c.discount_value}%` : `${c.discount_value} CHF`}
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{(c.applies_to || []).map((a) => APPLIES_LABEL[a] || a).join(', ')}</td>
                <td className="px-4 py-3">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(c.code, c.active)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${c.active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-700 text-zinc-400'}`}
                    data-testid={`promo-toggle-active-${c.code}`}>
                    <Power className="w-3 h-3" /> {c.active ? 'Actif' : 'Désactivé'}
                  </button>
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs max-w-[160px] truncate">{c.note || '-'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => deleteCode(c.code)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`promo-delete-${c.code}`} title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
